/**
 * Widget delivery for an admin SSO login (CM-1918, part of CM-1840).
 *
 * A moderator who signs in from the widget on a customer's own site does it in a popup this SPA is
 * loaded into. The session cannot be handed over the way the console path hands it over: the widget
 * authenticates with a Bearer token it keeps in `localStorage`, not with cookies, and `localStorage`
 * is partitioned per origin — so nothing written here is visible on the customer's page.
 *
 * What crosses the boundary instead is the token pair, by `postMessage` to the window that opened
 * this one, targeted at the customer's origin. The exchange itself stays here, first-party, because
 * the `admin-sso` grant matches the signed `state` against the `VfSsoNonce` cookie on
 * `.viafoura.co`, which a cross-site request from the customer's origin would not carry.
 *
 * The destination is never `*`, and is never taken from a query parameter. It is read out of the
 * signed `state`, which auth-service validated against the site's registered redirect hosts before
 * signing. See the contract on CM-1840.
 */

/** The value of the `vf:delivery` state claim that means "post the pair back to the opener". */
export const WIDGET_DELIVERY = 'widget';

/**
 * The `type` every message on this path carries.
 *
 * The receiving page is the customer's own, beside their tags and vendors, so the widget checks
 * this alongside the sender's origin and the window identity rather than assuming every message
 * from this origin is a sign-in.
 */
export const WIDGET_MESSAGE_TYPE = 'viafoura:admin-sso:token';

/**
 * Whether the window that opened this one can still be reached.
 *
 * It cannot when the customer's page is served with `Cross-Origin-Opener-Policy: same-origin`: the
 * popup then opens in its own browsing-context group and `window.opener` is null, so no pair can
 * ever be delivered. That is a header on the customer's page and only they can change it —
 * `same-origin-allow-popups` is sufficient — so it has to be reported as the configuration error it
 * is rather than looking like a sign-in that failed.
 *
 * @returns {boolean} true when there is an opener to post to
 */
export function hasOpener() {
    return Boolean(window.opener);
}

/**
 * Posts a completed sign-in to the widget and closes this window.
 *
 * @param {string} returnOrigin the origin read from the verified signed state
 * @param {{access_token: string, refresh_token: string}} pair the minted token pair
 * @returns {boolean} true when the message was posted
 */
export function deliverPair(returnOrigin, pair) {
    return deliver(returnOrigin, {
        access_token: pair.access_token,
        refresh_token: pair.refresh_token,
    });
}

/**
 * Posts a refusal to the widget and closes this window.
 *
 * The code travels as-is so the receiver can tell apart the refusals that ask different things of
 * the moderator: `sso_account_not_linked` is resolved by their Viafoura contact linking the account
 * on that property, `login_banned` is not resolved by trying again at all, and
 * `sso_temporarily_unavailable` is.
 *
 * @param {string} returnOrigin the origin read from the verified signed state
 * @param {string} code the refusal code
 * @returns {boolean} true when the message was posted
 */
export function deliverError(returnOrigin, code) {
    return deliver(returnOrigin, { error: code });
}

function deliver(returnOrigin, payload) {
    if (!hasOpener() || !isDeliverableOrigin(returnOrigin)) {
        return false;
    }

    window.opener.postMessage({
        type: WIDGET_MESSAGE_TYPE,
        ...payload,
    }, returnOrigin);
    window.close();
    return true;
}

/**
 * Whether a value is a single https origin, and nothing else.
 *
 * auth-service validated this origin against the site's allow-list before signing it, so this is
 * not the authorising check — it is the guard that a malformed or unexpected claim cannot widen the
 * audience of a live moderator pair. `postMessage` treats anything that is not a valid origin as a
 * target it cannot match, but `*` it accepts, and a pair broadcast to every listener on any page is
 * the one outcome this path must never produce.
 *
 * @param {*} value the claim value to check
 * @returns {boolean} true when it is a single https origin
 */
function isDeliverableOrigin(value) {
    if (typeof value !== 'string' || value.length === 0) {
        return false;
    }
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && url.origin === value;
    }
    catch (error) {
        return false;
    }
}
